import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../../components/ui/Sidebar";
import Sidebar from "../../../components/ui/Sidebar";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import ExpenseTable from "./components/ExpenseTable";
import { useGetExpenses } from "./useGetExpenses";
import { useVoidExpense } from "./useVoidExpense";
import toast from "react-hot-toast";

const STATUS_FILTERS = ["All", "Unpaid", "Partial", "Paid", "Overdue"];

const EXPENSE_TYPES = [
  {
    id: "operating",
    icon: "Briefcase",
    iconBg: "bg-blue-100 dark:bg-blue-900/20",
    iconColor: "text-blue-600 dark:text-blue-400",
    title: "Operating Expense",
    description: "Expenses incurred for operating the business",
    examples: "e.g. Rent, Salaries, Petty Expenses",
    path: "/purchases/expenses/operating",
  },
  {
    id: "bill",
    icon: "ReceiptText",
    iconBg: "bg-violet-100 dark:bg-violet-900/20",
    iconColor: "text-violet-600 dark:text-violet-400",
    title: "Purchase Bill",
    description: "Log a bill for purchase of product or service",
    examples: "e.g. Local Purchase or Import of Goods / Services",
    path: "/purchases/expenses/bill",
  },
  {
    id: "equipment",
    icon: "Monitor",
    iconBg: "bg-teal-100 dark:bg-teal-900/20",
    iconColor: "text-teal-600 dark:text-teal-400",
    title: "Office Equipment",
    description: "An expense incurred for purchase of office equipment",
    examples: "e.g. Laptop, Printer, Furniture",
    path: "/purchases/expenses/equipment",
  },
];

// ── Type Picker Modal ─────────────────────────────────────────────────────────
const TypePickerModal = ({ onClose, onSelect }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.95, opacity: 0, y: 16 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={{ scale: 0.95, opacity: 0, y: 16 }}
      transition={{ duration: 0.22 }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-border w-full max-w-2xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="px-6 py-5 border-b border-border flex items-center justify-between bg-muted/30">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="PlusCircle" size={18} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Add New Expense
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              What kind of expense would you like to add?
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <Icon name="X" size={18} className="text-muted-foreground" />
        </button>
      </div>

      {/* Options */}
      <div className="p-6 space-y-3">
        {EXPENSE_TYPES.map((type, index) => (
          <motion.button
            key={type.id}
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.06 }}
            onClick={() => onSelect(type)}
            className="w-full flex items-start gap-4 p-4 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 text-left group"
          >
            <div
              className={`w-12 h-12 ${type.iconBg} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}
            >
              <Icon name={type.icon} size={22} className={type.iconColor} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground mb-0.5">
                {type.title}
              </p>
              <p className="text-sm text-muted-foreground mb-1">
                {type.description}
              </p>
              <p className="text-xs text-muted-foreground/70">
                {type.examples}
              </p>
            </div>
            <Icon
              name="ChevronRight"
              size={18}
              className="text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-1"
            />
          </motion.button>
        ))}
      </div>
    </motion.div>
  </motion.div>
);

// ── Main Expenses Content ─────────────────────────────────────────────────────
const ExpensesContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");
  const navigate = useNavigate();
  const { data: expensesData = [], isLoading, isError } = useGetExpenses();

  const [expenses, setExpenses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [showPicker, setShowPicker] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    if (expensesData.length) setExpenses(expensesData);
  }, [expensesData]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const filtered = expenses.filter((e) => {
    const matchSearch =
      !searchTerm.trim() ||
      e.vendorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.expenseNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus =
      statusFilter === "All" ||
      e.status?.toLowerCase() === statusFilter.toLowerCase();
    return matchSearch && matchStatus;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const voidExpense = useVoidExpense();
  const handleDelete = (id) =>
    voidExpense.mutate(
      { id, reason: "Voided from expenses list" },
      {
        onSuccess: () => toast.success("Expense voided"),
        onError: (err) => toast.error(err?.message || "Failed to void expense"),
      },
    );

  const handleSelect = (type) => {
    setShowPicker(false);
    navigate(type.path);
  };

  // Stats
  const totalAmount = expenses.reduce(
    (s, e) => s + (parseFloat(e.amount) || 0),
    0,
  );
  const totalDue = expenses.reduce(
    (s, e) => s + (parseFloat(e.amountDue) || 0),
    0,
  );
  const statusCounts = expenses.reduce((acc, e) => {
    acc[e.status] = (acc[e.status] || 0) + 1;
    return acc;
  }, {});

  if (isLoading) return <LoadingSkeleton isCollapsed={isCollapsed} />;

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
    >
      <main className="p-4 md:p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-1">
                {t("expenses.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("expenses.description")}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" size="md" iconName="Download">
                {t("actions.export")}
              </Button>
              <Button
                variant="default"
                size="md"
                iconName="Plus"
                onClick={() => setShowPicker(true)}
              >
                {t("expenses.addExpense")}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          {[
            {
              label: "Total Expenses",
              value: expenses.length,
              icon: "ReceiptText",
              color:
                "text-blue-500 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400",
            },
            {
              label: "Total Amount",
              value: `SR ${totalAmount.toLocaleString("en-SA", { minimumFractionDigits: 2 })}`,
              icon: "DollarSign",
              color:
                "text-violet-500 bg-violet-100 dark:bg-violet-900/20 dark:text-violet-400",
            },
            {
              label: "Total Due",
              value: `SR ${totalDue.toLocaleString("en-SA", { minimumFractionDigits: 2 })}`,
              icon: "AlertCircle",
              color:
                "text-red-500 bg-red-100 dark:bg-red-900/20 dark:text-red-400",
            },
            {
              label: "Paid",
              value: statusCounts["paid"] || 0,
              icon: "CheckCircle2",
              color:
                "text-green-500 bg-green-100 dark:bg-green-900/20 dark:text-green-400",
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-card rounded-xl p-4 shadow-sm border border-border flex items-center gap-3"
            >
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${stat.color}`}
              >
                <Icon name={stat.icon} size={20} />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold text-foreground truncate">
                  {stat.value}
                </p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Status Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08 }}
          className="mb-6"
        >
          <div className="flex gap-2 flex-wrap">
            {STATUS_FILTERS.map((status) => {
              const count =
                status === "All"
                  ? expenses.length
                  : statusCounts[status.toLowerCase()] || 0;
              return (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    statusFilter === status
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "bg-card text-muted-foreground hover:bg-muted border border-border"
                  }`}
                >
                  {status}
                  <span
                    className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      statusFilter === status
                        ? "bg-white/20"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-card rounded-xl p-5 shadow-sm border border-border mb-6"
        >
          <div className="relative">
            <Icon
              name="Search"
              size={18}
              className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("expenses.searchPlaceholder")}
              className="w-full ps-10 pe-10 py-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={15} />
              </button>
            )}
          </div>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <ExpenseTable expenses={paginated} onDelete={handleDelete} />
        </motion.div>

        {/* Empty State */}
        {filtered.length === 0 && !isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl p-12 text-center shadow-sm border border-border mt-4"
          >
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="ReceiptText" size={32} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t("expenses.emptyTitle")}
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || statusFilter !== "All"
                ? t("common.adjustFiltersOrSearch")
                : t("expenses.emptyDescription")}
            </p>
            {!searchTerm && statusFilter === "All" && (
              <Button
                variant="default"
                iconName="Plus"
                onClick={() => setShowPicker(true)}
              >
                {t("expenses.addExpense")}
              </Button>
            )}
          </motion.div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 px-6 py-4 bg-card rounded-xl border border-border flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, filtered.length)} of{" "}
              {filtered.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronLeft"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              />
              <span className="text-sm text-foreground px-2">
                {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronRight"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              />
            </div>
          </div>
        )}
      </main>

      <AnimatePresence>
        {showPicker && (
          <TypePickerModal
            onClose={() => setShowPicker(false)}
            onSelect={handleSelect}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const Expenses = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <ExpensesContent />
    </div>
  </SidebarProvider>
);

const LoadingSkeleton = ({ isCollapsed }) => (
  <div
    className={`transition-all duration-300 ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
  >
    <main className="p-4 md:p-6 animate-pulse">
      <div className="h-8 bg-muted rounded w-48 mb-2" />
      <div className="h-4 bg-muted rounded w-80 mb-8" />
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded-xl" />
        ))}
      </div>
      <div className="flex gap-2 mb-6">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-9 bg-muted rounded-lg w-24" />
        ))}
      </div>
      <div className="h-14 bg-muted rounded-xl mb-6" />
      <div className="bg-card rounded-xl border border-border p-6 space-y-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-14 bg-muted rounded" />
        ))}
      </div>
    </main>
  </div>
);

export default Expenses;
